import { useState, useEffect, useRef, useCallback, type FormEvent, type ChangeEvent } from 'react';
import './CreateOrganization.css';
import { useTranslation } from 'react-i18next';
import { fetchOrganizations, createOrganization, type OrganizationRecord } from '../../services/organizationApi';
import type { ApiError } from '../../services/api';

/* ── Helpers ─────────────────────────────────────────── */

/** Convert a human name into a URL-safe slug. */
function slugify(text: string): string {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')   // replace non-alphanum runs with hyphen
        .replace(/^-+|-+$/g, '');       // strip leading/trailing hyphens
}

/* Slug availability is validated server-side on form submit. */

function formatTimestamp(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/* ── Component ───────────────────────────────────────── */

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken';
type ViewMode = 'list' | 'create';

interface FormErrors {
    name?: string;
    slug?: string;
    email?: string;
    phone?: string;
}

export default function CreateOrganization() {
    const { t: _t } = useTranslation();

    /* --- view mode --- */
    const [viewMode, setViewMode] = useState<ViewMode>('list');

    /* --- list state --- */
    const [organizations, setOrganizations] = useState<OrganizationRecord[]>([]);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState('');

    /* --- form state --- */
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');

    /* --- UI state --- */
    const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [formError, setFormError] = useState('');

    /* Debounce timer ref */
    const slugTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /* ============== Load organizations ============== */
    const loadOrganizations = useCallback(async () => {
        setListLoading(true);
        setListError('');
        try {
            const data = await fetchOrganizations();
            setOrganizations(data);
        } catch (err) {
            const apiErr = err as ApiError;
            setListError(apiErr.message || 'Failed to load organizations.');
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOrganizations();
    }, [loadOrganizations]);

    /* ---- Auto-slugify when name changes ---- */
    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setName(value);
        if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));

        if (!slugManuallyEdited) {
            const generated = slugify(value);
            setSlug(generated);
            triggerSlugCheck(generated);
        }
    };

    /* ---- Manual slug editing ---- */
    const handleSlugChange = (e: ChangeEvent<HTMLInputElement>) => {
        // Allow only lowercase alphanumeric + hyphens
        const raw = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
        setSlug(raw);
        setSlugManuallyEdited(true);
        if (errors.slug) setErrors((prev) => ({ ...prev, slug: undefined }));
        triggerSlugCheck(raw);
    };

    /* ---- Slug status indicator (visual only, validated server-side) ---- */
    const triggerSlugCheck = useCallback((value: string) => {
        if (slugTimerRef.current) clearTimeout(slugTimerRef.current);

        if (!value) {
            setSlugStatus('idle');
            return;
        }

        // Show "available" after a brief pause (actual uniqueness checked on submit)
        setSlugStatus('checking');
        slugTimerRef.current = setTimeout(() => {
            setSlugStatus('available');
        }, 400);
    }, []);

    /* Cleanup debounce timer on unmount */
    useEffect(() => {
        return () => {
            if (slugTimerRef.current) clearTimeout(slugTimerRef.current);
        };
    }, []);

    /* ---- Validation ---- */
    const validate = (): boolean => {
        const newErrors: FormErrors = {};

        if (!name.trim()) {
            newErrors.name = 'Organization name is required';
        } else if (name.trim().length > 255) {
            newErrors.name = 'Name must be 255 characters or fewer';
        }

        if (!slug.trim()) {
            newErrors.slug = 'Organization URL is required';
        } else if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) {
            newErrors.slug = 'URL must be lowercase alphanumeric with hyphens only';
        }

        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'Enter a valid email address';
        }

        if (phone && phone.length > 20) {
            newErrors.phone = 'Phone must be 20 characters or fewer';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* ---- Submit ---- */
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);
        setFormError('');

        try {
            await createOrganization({
                name: name.trim(),
                slug: slug.trim(),
                email: email.trim() || null,
                phone: phone.trim() || null,
            });
            setSubmitted(true);
        } catch (err) {
            const apiErr = err as ApiError;
            // Map Laravel field-level validation errors
            if (apiErr.errors) {
                const fieldErrors: FormErrors = {};
                if (apiErr.errors.name) fieldErrors.name = apiErr.errors.name[0];
                if (apiErr.errors.slug) {
                    fieldErrors.slug = apiErr.errors.slug[0];
                    setSlugStatus('taken');
                }
                if (apiErr.errors.email) fieldErrors.email = apiErr.errors.email[0];
                if (apiErr.errors.phone) fieldErrors.phone = apiErr.errors.phone[0];
                setErrors(fieldErrors);
            }
            setFormError(apiErr.message || 'Failed to create organization.');
            setTimeout(() => setFormError(''), 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ---- Reset form ---- */
    const handleReset = () => {
        setName('');
        setSlug('');
        setSlugManuallyEdited(false);
        setEmail('');
        setPhone('');
        setSlugStatus('idle');
        setErrors({});
        setSubmitted(false);
        setFormError('');
    };

    /* ---- Switch to create mode ---- */
    const openCreateForm = () => {
        handleReset();
        setViewMode('create');
    };

    /* ---- Back to list ---- */
    const backToList = () => {
        handleReset();
        setViewMode('list');
        loadOrganizations();
    };

    /* ────────────────── Render ────────────────── */

    /* ═══════ List View ═══════ */
    if (viewMode === 'list') {
        return (
            <div className="create-org">
                {/* Header */}
                <div className="create-org__list-header">
                    <div>
                        <h1 className="create-org__title">Organizations</h1>
                        <p className="create-org__subtitle">
                            Manage your organizations on Gurukul.
                        </p>
                    </div>
                    <button className="create-org__submit" onClick={openCreateForm}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Add Organization
                    </button>
                </div>

                {/* Error */}
                {listError && (
                    <div className="create-org__toast create-org__toast--error">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                        <span>{listError}</span>
                        <button className="create-org__toast-dismiss" onClick={() => setListError('')} aria-label="Dismiss">✕</button>
                    </div>
                )}

                {/* Content */}
                <div className="create-org__list-body">
                    {listLoading ? (
                        <div className="create-org__loading">
                            <div className="create-org__loader">
                                <span className="create-org__loader-dot" />
                                <span className="create-org__loader-dot" />
                                <span className="create-org__loader-dot" />
                            </div>
                        </div>
                    ) : organizations.length === 0 ? (
                        <div className="create-org__empty">
                            <div className="create-org__empty-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                    <path d="M9 21V9h6v12" />
                                    <path d="M3 9h18" />
                                </svg>
                            </div>
                            <h3 className="create-org__empty-title">No organizations yet</h3>
                            <p className="create-org__empty-text">
                                Create your first organization to get started.
                            </p>
                        </div>
                    ) : (
                        <div className="create-org__org-list">
                            {organizations.map((org) => (
                                <div key={org.id} className="create-org__org-card">
                                    <div className="create-org__org-card-header">
                                        <div className="create-org__org-avatar">
                                            {org.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="create-org__org-info">
                                            <span className="create-org__org-name">{org.name}</span>
                                            <span className="create-org__org-slug">app.gurukulsync.com/{org.slug}</span>
                                        </div>
                                        <span
                                            className={`create-org__org-status create-org__org-status--${org.status}`}
                                        >
                                            {org.status}
                                        </span>
                                    </div>

                                    <div className="create-org__org-details">
                                        {org.email && (
                                            <div className="create-org__org-detail">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                    <polyline points="22,6 12,13 2,6" />
                                                </svg>
                                                <span>{org.email}</span>
                                            </div>
                                        )}
                                        {org.phone && (
                                            <div className="create-org__org-detail">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                                </svg>
                                                <span>{org.phone}</span>
                                            </div>
                                        )}
                                        {org.city && (
                                            <div className="create-org__org-detail">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                                    <circle cx="12" cy="10" r="3" />
                                                </svg>
                                                <span>
                                                    {[org.city, org.state].filter(Boolean).join(', ')}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="create-org__org-footer">
                                        <span className="create-org__org-meta">
                                            {org.creator
                                                ? `Created by ${org.creator.first_name} ${org.creator.last_name}`
                                                : 'Created'}
                                        </span>
                                        <span className="create-org__org-meta">
                                            {formatTimestamp(org.created_at)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    /* ═══════ Create View ═══════ */
    return (
        <div className="create-org">
            {/* Back button */}
            <button className="create-org__back-btn" onClick={backToList}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                </svg>
                Back to Organizations
            </button>

            {/* Page title */}
            <div className="create-org__header">
                <h1 className="create-org__title">Create Organization</h1>
                <p className="create-org__subtitle">
                    Set up your organization to get started with Gurukul.
                </p>
            </div>

            {/* Error toast */}
            {formError && (
                <div className="create-org__toast create-org__toast--error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    <span>{formError}</span>
                    <button className="create-org__toast-dismiss" onClick={() => setFormError('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* Success toast */}
            {submitted && (
                <div className="create-org__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    Organization created successfully!
                    <button
                        type="button"
                        className="create-org__cancel"
                        style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: '0.8125rem' }}
                        onClick={handleReset}
                    >
                        Create Another
                    </button>
                </div>
            )}

            {/* Form card */}
            {!submitted && (
                <div className="create-org__card">
                    <form className="create-org__form" onSubmit={handleSubmit} noValidate>

                        {/* Organization Name */}
                        <div className="create-org__field">
                            <label className="create-org__label create-org__label--required" htmlFor="orgName">
                                Organization Name
                            </label>
                            <div className="create-org__input-wrap">
                                <span className="create-org__input-icon">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                        <path d="M9 21V9h6v12" />
                                        <path d="M3 9h18" />
                                    </svg>
                                </span>
                                <input
                                    className={`create-org__input ${errors.name ? 'create-org__input--error' : ''}`}
                                    type="text"
                                    id="orgName"
                                    placeholder="e.g. Acme Corporation"
                                    value={name}
                                    onChange={handleNameChange}
                                    maxLength={255}
                                    autoFocus
                                />
                            </div>
                            {errors.name && <p className="create-org__error">{errors.name}</p>}
                        </div>

                        {/* Slug */}
                        <div className="create-org__field">
                            <label className="create-org__label create-org__label--required" htmlFor="orgSlug">
                                Organization URL
                            </label>
                            <div className={`create-org__slug-wrap ${errors.slug ? 'create-org__slug-wrap--error' : ''}`}>
                                <span className="create-org__slug-prefix">app.gurukulsync.com/</span>
                                <input
                                    className="create-org__slug-input"
                                    type="text"
                                    id="orgSlug"
                                    placeholder="your-org"
                                    value={slug}
                                    onChange={handleSlugChange}
                                />
                                {slugStatus !== 'idle' && (
                                    <span className={`create-org__slug-status create-org__slug-status--${slugStatus}`}>
                                        {slugStatus === 'checking' && (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <circle cx="12" cy="12" r="10" strokeDasharray="50" strokeDashoffset="35" strokeLinecap="round" />
                                            </svg>
                                        )}
                                        {slugStatus === 'available' && (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        )}
                                        {slugStatus === 'taken' && (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="18" y1="6" x2="6" y2="18" />
                                                <line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                        )}
                                    </span>
                                )}
                            </div>
                            {errors.slug ? (
                                <p className="create-org__error">{errors.slug}</p>
                            ) : slugStatus === 'taken' ? (
                                <p className="create-org__error">This URL is already taken</p>
                            ) : (
                                <p className="create-org__helper">Only lowercase letters, numbers, and hyphens.</p>
                            )}
                        </div>

                        <hr className="create-org__divider" />

                        {/* Contact Email & Phone — side by side */}
                        <div className="create-org__row">
                            <div className="create-org__field">
                                <label className="create-org__label" htmlFor="orgEmail">
                                    Contact Email
                                </label>
                                <div className="create-org__input-wrap">
                                    <span className="create-org__input-icon">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                            <polyline points="22,6 12,13 2,6" />
                                        </svg>
                                    </span>
                                    <input
                                        className={`create-org__input ${errors.email ? 'create-org__input--error' : ''}`}
                                        type="email"
                                        id="orgEmail"
                                        placeholder="admin@example.com"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                                        }}
                                    />
                                </div>
                                {errors.email && <p className="create-org__error">{errors.email}</p>}
                            </div>

                            <div className="create-org__field">
                                <label className="create-org__label" htmlFor="orgPhone">
                                    Contact Phone
                                </label>
                                <div className="create-org__input-wrap">
                                    <span className="create-org__input-icon">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                        </svg>
                                    </span>
                                    <input
                                        className={`create-org__input ${errors.phone ? 'create-org__input--error' : ''}`}
                                        type="tel"
                                        id="orgPhone"
                                        placeholder="+91 98765 43210"
                                        value={phone}
                                        maxLength={20}
                                        onChange={(e) => {
                                            setPhone(e.target.value);
                                            if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
                                        }}
                                    />
                                </div>
                                {errors.phone && <p className="create-org__error">{errors.phone}</p>}
                            </div>
                        </div>

                        <hr className="create-org__divider" />

                        {/* Actions */}
                        <div className="create-org__actions">
                            <button
                                className="create-org__submit"
                                type="submit"
                                disabled={isSubmitting || slugStatus === 'checking'}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="create-org__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" strokeWidth="3" />
                                        </svg>
                                        Creating…
                                    </>
                                ) : (
                                    'Create Organization'
                                )}
                            </button>
                            <button
                                className="create-org__cancel"
                                type="button"
                                onClick={handleReset}
                                disabled={isSubmitting}
                            >
                                Reset
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
